import { stateAbstract, actionsAbstract, mutationsAbstract } from '@/store/abstract'
import { auth as api, account as accountApi } from '@/backend/api'
import Vue from 'vue'

const state = {
  accountsDetail: {},
  accountsCount: null,
  accountsList: [],
  onboardingsList: [],
  emailsDetail: {},
  emailsCount: null,
  emailsList: [],
  banksDetail: {},
  banksCount: null,
  banksList: [],
  invoicesDetail: {},
  resolutionsDetail: {},
  resolutionsList: [],
  membersCount: null,
  membersList: [],
  membersDetail: {},
  invitationsCount: null,
  invitationsList: [],
  invitationsDetail: {},
  filtersDetail: [],
  filtersCount: null,
  filtersList: [],
  searchCount: null,
  searchList: [],
  tokensList: [],
  notificationsList: [],
  notificationsCount: null,
  notificationsUnread: 0,
  // webhooks
  conversationsCount: null,
  conversationsList: [],
  eventsCount: null,
  eventsList: [],
  webhooksCount: null,
  webhooksDetail: {},
  webhooksList: [],
  // end webhooks
  // billing
  paymentsList: [],
  sourcesList: [],
  // end billing
  ...stateAbstract
}

const actions = {
  /**
   * Unread badge counter.
   *
   * Counted by the API rather than derived from the loaded page: the dropdown
   * only holds the first 20 notifications, so counting in the client
   * under-reports as soon as there are more.
   */
  NOTIFICATIONS_UNREAD_COUNT: ({ commit }) => {
    return accountApi.list('notifications/unread-count')
    .then(response => {
      commit('SET_NOTIFICATIONS_UNREAD', response.data.count)
      return response.data.count
    })
    .catch(() => {
      // The bell is ambient UI: a failed count must not surface an error.
      return null
    })
  },

  NOTIFICATIONS_MARK_ALL_READ: ({ commit }) => {
    return accountApi.create('notifications/mark-all-read', {})
    .then(response => {
      commit('SET_NOTIFICATIONS_UNREAD', 0)
      return response
    })
  },

  BULK_INVITATION: ({ commit }, { persons }) => {
    return new Promise((resolve, reject) => {
      commit('TOGGLE_CREATE_LOADER')
      api.bulkInvitation(persons)
      .then(() => {
        resolve()
      })
      .catch((error) => {
        reject(error)
      })
      .finally(() => {
        commit('TOGGLE_CREATE_LOADER')
      })
    })
  },
  SEARCH_LIST: ({ commit }, { resource, query, loader = true }) => {
    return new Promise((resolve, reject) => {
      if (loader) {
        commit('TOGGLE_RETRIEVE_LOADER')
      }
      accountApi.list(resource, query)
      .then(response => {
        commit('SET_SEARCH', response)
        resolve(response)
      })
      .catch(error => {
        reject(error)
      })
      .finally(() => {
        if (loader) {
          commit('TOGGLE_RETRIEVE_LOADER')
        }
      })
    })
  },

  CLEAR_LIST: ({ commit }) => {
    commit('CLEAR_LIST')
  },
  ...actionsAbstract
}
const mutations = {
  SET_NOTIFICATIONS_UNREAD: (state, value) => {
    Vue.set(state, 'notificationsUnread', value)
  },

  ADJUST_NOTIFICATIONS_UNREAD: (state, delta) => {
    Vue.set(state, 'notificationsUnread', Math.max(0, state.notificationsUnread + delta))
  },

  PREPEND_NOTIFICATION: (state, notification) => {
    // Guard against the same notification arriving twice (a Pusher event that
    // races the initial fetch, or a reconnect replay).
    if (state.notificationsList.some((item) => item.id === notification.id)) {
      return
    }
    Vue.set(state, 'notificationsList', [notification, ...state.notificationsList])
    Vue.set(state, 'notificationsCount', (state.notificationsCount || 0) + 1)
  },

  APPEND_NOTIFICATIONS: (state, notifications) => {
    const known = new Set(state.notificationsList.map((item) => item.id))
    const fresh = notifications.filter((item) => !known.has(item.id))
    Vue.set(state, 'notificationsList', [...state.notificationsList, ...fresh])
  },

  SET_SEARCH: (state, response) => {
    Vue.set(state, 'searchList', response.data.hits.hits)
    Vue.set(state, 'searchCount', response.data.hits.total.value)
  },

  CLEAR_LIST: (state) => {
    Vue.set(state, 'searchList', [])
    Vue.set(state, 'searchCount', 0)
  },
  ...mutationsAbstract
}

export default {
  namespaced: true,
  state,
  actions,
  mutations
}
